import {
  Component, computed, contentChild, ElementRef, HostListener, inject,
  input, signal, TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { cn } from './cn';

/**
 * Simple anchored menu — replaces MatMenu.
 *
 *   <hlm-menu>
 *     <button hlmMenuTrigger>Open</button>
 *     <ng-template hlmMenuContent>
 *       <button hlmMenuItem>Edit</button>
 *       <button hlmMenuItem>Delete</button>
 *     </ng-template>
 *   </hlm-menu>
 */
@Component({
  selector: 'hlm-menu',
  standalone: true,
  imports: [NgTemplateOutlet],
  template: `
    <div class="relative inline-block">
      <div (click)="toggle()"><ng-content select="[hlmMenuTrigger]" /></div>
      @if (open()) {
        <div
          [class]="_panelClass()"
          role="menu"
          (click)="$event.stopPropagation()"
        >
          <ng-container *ngTemplateOutlet="content() ?? null" />
        </div>
      }
    </div>
  `,
})
export class HlmMenuComponent {
  readonly align = input<'start' | 'end'>('end');
  readonly width = input<string>('min-w-[12rem]');
  readonly userClass = input<string>('', { alias: 'class' });

  readonly content = contentChild(HlmMenuContentDirective, { read: TemplateRef });
  readonly open = signal(false);

  private el = inject(ElementRef);

  protected _panelClass = computed(() =>
    cn(
      'absolute top-full z-50 mt-1.5 origin-top rounded-lg border border-border bg-popover p-1 shadow-[0_2px_4px_rgba(20,18,30,0.04),0_24px_48px_-12px_rgba(20,18,30,0.18)]',
      this.align() === 'end' ? 'right-0' : 'left-0',
      this.width(),
      this.userClass(),
    ),
  );

  toggle(): void { this.open.update(v => !v); }
  close(): void { this.open.set(false); }

  @HostListener('document:click', ['$event'])
  onDoc(e: MouseEvent): void {
    if (this.open() && !this.el.nativeElement.contains(e.target)) this.close();
  }

  @HostListener('document:keydown.escape')
  onEsc(): void { this.close(); }
}

import { Directive } from '@angular/core';

@Directive({
  selector: '[hlmMenuContent]',
  standalone: true,
})
export class HlmMenuContentDirective {}

@Directive({
  selector: 'button[hlmMenuItem], a[hlmMenuItem]',
  standalone: true,
  host: {
    '[class]': '_c()',
    role: 'menuitem',
  },
})
export class HlmMenuItemDirective {
  readonly tone = input<'default' | 'destructive'>('default');
  readonly userClass = input<string>('', { alias: 'class' });
  protected _c = computed(() =>
    cn(
      'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-base leading-none transition-colors',
      'focus:outline-none focus-visible:bg-muted',
      this.tone() === 'destructive'
        ? 'text-foreground hover:bg-destructive/10 hover:text-destructive'
        : 'text-foreground hover:bg-muted',
      this.userClass(),
    ),
  );
}

export const HLM_MENU = [HlmMenuComponent, HlmMenuContentDirective, HlmMenuItemDirective] as const;
